import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Users,
  Calendar,
  Clock,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { format, parseISO } from 'date-fns';

const ParentSmallGroup = () => {
  const [loading, setLoading] = useState(true);
  const [upcomingSlots, setUpcomingSlots] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [children, setChildren] = useState([]);
  
  // Registration dialog
  const [registerDialog, setRegisterDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [registering, setRegistering] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [slotsRes, regsRes, childrenRes] = await Promise.all([
        api.get('/small-group/slots/upcoming?limit=20'),
        api.get('/small-group/registrations/my'),
        api.get('/auth/me/children')
      ]);
      
      setUpcomingSlots(slotsRes.data || []);
      setMyRegistrations(regsRes.data || []);
      setChildren(childrenRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegister = async () => {
    if (!selectedSlot || !selectedChild) {
      toast.error('Please select a child');
      return;
    }
    if (!termsAccepted) {
      toast.error('Please agree to the Terms & Conditions');
      return;
    }

    setRegistering(true);
    try {
      await api.post('/small-group/registrations', {
        slot_id: selectedSlot.id,
        player_id: selectedChild,
        terms_accepted: true,
      });
      toast.success('Registration successful!');
      setRegisterDialog(false);
      setSelectedSlot(null);
      setSelectedChild('');
      setTermsAccepted(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async (registrationId) => {
    if (!window.confirm('Are you sure you want to cancel this registration?')) return;
    
    try {
      await api.delete(`/small-group/registrations/${registrationId}`);
      toast.success('Registration cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel registration');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      registered: 'bg-blue-100 text-blue-800',
      attended: 'bg-green-100 text-green-800',
      no_show: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-800',
      pending: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  // Filter out children already registered for selected slot
  const availableChildren = selectedSlot 
    ? children.filter(child => 
        !myRegistrations.some(r => 
          r.slot_id === selectedSlot.id && 
          r.player_id === child.id && 
          r.status !== 'cancelled'
        )
      )
    : children;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Small Group Sessions</h1>
          <p className="text-slate-500">Register your children for private coaching sessions</p>
        </div>

        {/* Available Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Available Sessions
            </CardTitle>
            <CardDescription>
              Click on a session to register your child
            </CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingSlots.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No sessions available for registration at the moment.</p>
                <p className="text-sm">Check back later for new sessions.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {upcomingSlots.map(slot => (
                  <Card key={slot.id} className="border-2 hover:border-blue-200 transition-colors">
                    <CardContent className="pt-4">
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-semibold text-lg">{slot.session_name}</h3>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <User className="h-4 w-4" />
                            Coach: {slot.coach_name}
                          </p>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <p className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {format(parseISO(slot.date), 'EEEE, MMMM d, yyyy')}
                          </p>
                          <p className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            {slot.start_time} - {slot.end_time} ({slot.duration_hours}h)
                          </p>
                          {slot.location && (
                            <p className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              {slot.location}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">
                              {slot.available_spots} spots left
                            </span>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => {
                              setSelectedSlot(slot);
                              setSelectedChild('');
                              setTermsAccepted(false);
                              setRegisterDialog(true);
                            }}
                            disabled={slot.available_spots === 0}
                          >
                            {slot.available_spots === 0 ? 'Full' : 'Register'}
                          </Button>
                        </div>
                        
                        {slot.description && (
                          <p className="text-sm text-slate-600 pt-2 border-t">
                            {slot.description}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Registrations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              My Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myRegistrations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No registrations yet.</p>
              </div>
            ) : (
            <div className="overflow-x-auto -mx-4 lg:mx-0">
              <Table className="min-w-[500px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs lg:text-sm">Session</TableHead>
                    <TableHead className="text-xs lg:text-sm whitespace-nowrap">Date & Time</TableHead>
                    <TableHead className="text-xs lg:text-sm">Child</TableHead>
                    <TableHead className="text-xs lg:text-sm">Status</TableHead>
                    <TableHead className="text-right text-xs lg:text-sm">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myRegistrations.map(reg => (
                    <TableRow key={reg.id}>
                      <TableCell className="text-xs lg:text-sm">
                        <div>
                          <p className="font-medium">{reg.session_name}</p>
                          <p className="text-[10px] lg:text-xs text-slate-500">{reg.coach_name}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs lg:text-sm whitespace-nowrap">
                        <p>{reg.slot_date}</p>
                        <p className="text-[10px] lg:text-xs text-slate-500">
                          {reg.slot_start_time} - {reg.slot_end_time}
                        </p>
                      </TableCell>
                      <TableCell className="text-xs lg:text-sm">{reg.player_name}</TableCell>
                      <TableCell className="text-xs lg:text-sm">{getStatusBadge(reg.status)}</TableCell>
                      <TableCell className="text-right">
                        {reg.status === 'registered' && reg.slot_status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => handleCancelRegistration(reg.id)}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            )}
          </CardContent>
        </Card>

        {/* Note about invoices in billing */}
        <div className="text-center text-sm text-slate-500 py-4">
          <p>View and pay your Small Group Session invoices in the <a href="/parent/billing?tab=invoices" className="text-blue-600 hover:underline font-medium">Billing → Invoices</a> section.</p>
        </div>

        {/* Registration Dialog */}
        <Dialog open={registerDialog} onOpenChange={setRegisterDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Register for Session</DialogTitle>
            </DialogHeader>
            {selectedSlot && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <h3 className="font-semibold">{selectedSlot.session_name}</h3>
                  <p className="text-sm text-slate-600">Coach: {selectedSlot.coach_name}</p>
                  <p className="text-sm">
                    <strong>Date:</strong> {format(parseISO(selectedSlot.date), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm">
                    <strong>Time:</strong> {selectedSlot.start_time} - {selectedSlot.end_time}
                  </p>
                  <p className="text-sm">
                    <strong>Duration:</strong> {selectedSlot.duration_hours} hours
                  </p>
                  <p className="text-sm text-green-600">
                    {selectedSlot.available_spots} spots available
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Select Child to Register</label>
                  {availableChildren.length === 0 ? (
                    <p className="text-sm text-amber-600 mt-2">
                      All your children are already registered for this session.
                    </p>
                  ) : (
                    <Select value={selectedChild} onValueChange={setSelectedChild}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a child" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableChildren.map(child => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                <p className="text-sm text-slate-500">
                  Note: You will be billed after the session based on actual attendance and number of participants.
                </p>

                {/* Terms & Conditions consent */}
                <div
                  className={`flex items-start gap-2 p-3 rounded-md border ${
                    termsAccepted ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
                  }`}
                  data-testid="coaching-terms-block"
                >
                  <Checkbox
                    id="coaching-terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(!!checked)}
                    data-testid="coaching-terms-checkbox"
                    className="mt-0.5"
                  />
                  <Label htmlFor="coaching-terms" className="text-sm cursor-pointer leading-snug">
                    I agree to the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline"
                      data-testid="coaching-terms-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms &amp; Conditions
                    </a>
                    <span className="text-rose-600"> *</span>
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRegisterDialog(false); setTermsAccepted(false); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleRegister} 
                disabled={!selectedChild || !termsAccepted || registering}
                data-testid="coaching-confirm-btn"
              >
                {registering ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Confirm Registration'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentSmallGroup;
